const normalizeBaseUrl = (value) => String(value || '').trim().replace(/\/+$/, '');

const ensureTrailingSlash = (value) => (value.endsWith('/') ? value : `${value}/`);

const isLoopbackHost = (value) => ['localhost', '127.0.0.1'].includes(String(value || '').trim().toLowerCase());
const isIpAddress = (value) => /^\d{1,3}(\.\d{1,3}){3}$/.test(String(value || '').trim().toLowerCase());
const stripCommonSubdomain = (value = '') => String(value || '').trim().replace(/^(www|portal|api)\./i, '');

const DEFAULT_CBT_BASE_URL = 'https://cbt.steeschools.com';

const shouldUseConfiguredBaseUrl = (value) => {
  if (!value) {
    return false;
  }

  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const configuredUrl = new URL(value);
    const currentHost = window.location?.hostname || '';

    if (!isLoopbackHost(currentHost) && isLoopbackHost(configuredUrl.hostname)) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

const normalizePortalRole = (value) => {
  const role = String(value || '').trim().toLowerCase();

  if (role === 'admin') {
    return 'admin';
  }

  if (role === 'student') {
    return 'student';
  }

  if (role === 'parent') {
    return 'parent';
  }

  if (['applicant', 'admission', 'admissions', 'apply'].includes(role)) {
    return 'applicant';
  }

  return 'educator';
};

const inferPortalBaseUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const { protocol, hostname } = window.location;
  const lowerHost = String(hostname || '').trim().toLowerCase();

  if (isLoopbackHost(lowerHost) || isIpAddress(lowerHost) || !lowerHost.includes('.')) {
    return `${protocol}//${hostname}:3001`;
  }

  if (lowerHost.startsWith('portal.')) {
    return `${protocol}//${hostname}`;
  }

  const baseHost = stripCommonSubdomain(hostname) || hostname;
  return `${protocol}//portal.${baseHost}`;
};

const derivePortalBaseUrlFromWebsiteBaseUrl = (value) => {
  try {
    const url = new URL(value);
    const lowerHost = String(url.hostname || '').trim().toLowerCase();

    if (isLoopbackHost(lowerHost) || isIpAddress(lowerHost) || !lowerHost.includes('.')) {
      if (!url.port || url.port === '3000' || url.port === '3002') {
        url.port = '3001';
      }
      return normalizeBaseUrl(url.toString());
    }

    if (lowerHost.startsWith('portal.')) {
      return normalizeBaseUrl(url.toString());
    }

    const baseHost = stripCommonSubdomain(url.hostname) || url.hostname;
    url.hostname = `portal.${baseHost}`;
    return normalizeBaseUrl(url.toString());
  } catch (error) {
    return '';
  }
};

export const getPortalBaseUrl = () => {
  // Prefer explicit configuration.
  // - REACT_APP_PORTAL_BASE_URL should include protocol+host+port.
  const configuredPortalBaseUrl = normalizeBaseUrl(process.env.REACT_APP_PORTAL_BASE_URL);
  if (shouldUseConfiguredBaseUrl(configuredPortalBaseUrl)) {
    return configuredPortalBaseUrl;
  }

  // Optional: let devs still configure via WEBSITE_BASE_URL and we derive portal from it.
  // (If WEBSITE_BASE_URL ends with :3000 we’ll switch to :3001, but only as an explicit derivation.)
  const configuredWebsiteBaseUrl = normalizeBaseUrl(process.env.REACT_APP_WEBSITE_BASE_URL);
  if (shouldUseConfiguredBaseUrl(configuredWebsiteBaseUrl)) {
    const derivedFromWebsite = derivePortalBaseUrlFromWebsiteBaseUrl(configuredWebsiteBaseUrl);
    if (derivedFromWebsite) {
      return derivedFromWebsite;
    }
  }

  // Last resort (no env): legacy-ish inference.
  return normalizeBaseUrl(inferPortalBaseUrl());
};


export const getPortalLink = (role) => {
  const normalizedRole = normalizePortalRole(role);
  const routeByRole = {
    admin: 'adminLogin',
    educator: 'login',
    student: 'student-portal',
    parent: 'student-portal',
    applicant: 'applicant-portal',
  };
  const url = new URL(routeByRole[normalizedRole] || 'login', ensureTrailingSlash(getPortalBaseUrl()));

  if (normalizedRole === 'student' || normalizedRole === 'parent') {
    url.searchParams.set('role', normalizedRole);
  }

  return url.toString();
};

export const getCbtBaseUrl = () => {
  const configuredCbtBaseUrl = normalizeBaseUrl(process.env.REACT_APP_CBT_BASE_URL);
  if (shouldUseConfiguredBaseUrl(configuredCbtBaseUrl)) {
    return configuredCbtBaseUrl;
  }

  return DEFAULT_CBT_BASE_URL;
};

export const getCbtLink = (path = '') => {
  const normalizedPath = String(path || '').replace(/^\/+/, '');
  return new URL(normalizedPath, ensureTrailingSlash(getCbtBaseUrl())).toString();
};
